// Behavior-level content. Describes what the validator reports and how to act,
// not the internal validation code, cross-node algorithm, or fetch details.

export default {
  title: 'Schain Validator',
  summary:
    "Validate a SupplyChain object end to end — structure, every node against sellers.json, node ordering, and authorization against the publisher's ads.txt / app-ads.txt.",

  sections: [
    {
      h: 'What this tool does',
      p: [
        "Checks a schain's required fields and each node's required fields, flags duplicates, and looks up every node's seller ID in that ad system's `sellers.json` to confirm it exists and is the expected type.",
        'It validates node order against the IAB spec — the first node should be the publisher / direct seller, intermediate nodes should be resellers — and flags chains that look like they\u2019re missing a hop.',
        "When you supply a store URL or publisher domain, it resolves the developer domain and checks whether each node is actually authorized in the publisher's `ads.txt` (web) or `app-ads.txt` (apps), including the DIRECT-vs-RESELLER rule for non-first nodes.",
      ],
    },
    {
      h: 'When to use it',
      list: [
        'A buyer flagged your supply path and you need to see exactly which node breaks.',
        "You're building or reviewing a chain and want every hop documented and authorized before it goes live.",
        'You want to know whether a partial chain (`complete=0`) is the actual problem.',
      ],
    },
    {
      h: 'How to use it',
      steps: [
        '**Paste the schain** — or paste the whole bid request and the tool will find the schain wherever it lives.',
        '**Add the inventory source** if you have it: a store URL for app inventory, or the publisher domain for web. This unlocks the authorization check; without it you still get structure and sellers.json validation.',
        '**Read the per-node results.** Each node shows whether its seller ID resolves in sellers.json, whether it\u2019s authorized in the right file, and whether its position and relationship in the chain are valid.',
        '**Fix from the top.** Ordering and cross-node issues tend to cascade — resolving the earliest broken node often clears the ones below it.',
      ],
    },
    {
      callout:
        'For app inventory only `app-ads.txt` counts; for web only `ads.txt` counts. A node authorized in the wrong file for the context still reads as unauthorized — that\u2019s intentional.',
    },
  ],

  faqs: [
    {
      q: 'complete=0 — is that an error?',
      a: "Not structurally, but it tells buyers the chain is partial — not every intermediary is listed. Many platforms reduce or block spend on incomplete chains, so it's surfaced as something to resolve.",
    },
    {
      q: 'A node resolves in sellers.json but still shows unauthorized.',
      a: "Existence and authorization are separate checks. A seller can be real and still not be listed in the publisher's ads.txt / app-ads.txt for this inventory — or listed as DIRECT when, as a non-first node, it should be RESELLER.",
    },
    {
      q: "It couldn't check authorization.",
      a: 'That happens when the store URL or domain did not resolve to a developer domain, so there was no ads.txt to check against. The structure and sellers.json results are still valid.',
    },
  ],
};
